import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// V11.9 — Cache utilisateur en localStorage pour survivre :
//  - aux reboot du preview (backend momentanément unreachable)
//  - aux latences /auth/me au boot (l'UI a un user tout de suite)
//  - aux redirections post-Google OAuth (pas de flash "déconnecté")
const USER_CACHE_KEY = "aito_user_cache";

function readUserCache() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.id && parsed.email) return parsed;
  } catch { /* ignore */ }
  return null;
}
function writeUserCache(user) {
  try {
    if (user && user.id) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } catch { /* ignore */ }
}
function clearUserCache() {
  try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  // V11.9 — Initialise avec le cache pour éviter tout flash déconnecté après refresh
  const cachedUser = (() => {
    try {
      const token = localStorage.getItem("aito_token");
      return token ? readUserCache() : null;
    } catch { return null; }
  })();
  const [user, setUser] = useState(cachedUser);
  // Si on a déjà un user en cache, `loading` peut être false : l'UI est
  // immédiatement disponible et /auth/me se contentera de rafraîchir en tâche
  // de fond. Sinon (pas de cache), on reste en loading pour éviter un flash.
  const [loading, setLoading] = useState(!cachedUser);

  useEffect(() => {
    const token = (() => { try { return localStorage.getItem("aito_token"); } catch { return null; } })();
    if (!token) { setLoading(false); return; }
    // V11.8/V11.9 — Session résiliente : on ne purge le token QUE si le
    // backend répond explicitement 401. Erreur réseau/timeout → on garde le
    // token + le user en cache, l'utilisateur reste connecté et la prochaine
    // requête réussira.
    api.get("/auth/me")
      .then((r) => { setUser(r.data); writeUserCache(r.data); })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          try { localStorage.removeItem("aito_token"); } catch { /* ignore */ }
          clearUserCache();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const persist = (token, u) => {
    try { localStorage.setItem("aito_token", token); } catch { /* ignore */ }
    writeUserCache(u);
    setUser(u);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.token, data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    persist(data.token, data.user);
    return data.user;
  };

  const googleSession = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    persist(data.token, data.user);
    return data.user;
  };

  const logout = () => {
    try { localStorage.removeItem("aito_token"); } catch { /* ignore */ }
    clearUserCache();
    setUser(null);
  };

  const updateFavorites = async (favorite_disciplines) => {
    await api.patch("/auth/me/favorites", { favorite_disciplines });
    setUser((u) => {
      const next = { ...u, favorite_disciplines };
      writeUserCache(next);
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleSession, logout, updateFavorites }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
